// The foreground over a colour of the organization's brand (D-174).
//
// A customer's colour is never refused and never corrected: what the brand
// decides is the surface, and what the product decides is the text over it.
// The choice is the one with more contrast, measured as WCAG 2.1 does, and
// it is written to the root beside the colour itself, so a component only
// ever writes `color: var(--brand-on-accent)`.
//
// Both accents this release was verified against sit under 3:1 against
// white, so both take the dark foreground: white over them would be the
// worse of the two readings, not the better one.

/** The dark foreground: the product's own ink, never a brand colour. */
export const DARK_FOREGROUND = '#0b2523';
/** The light foreground. */
export const LIGHT_FOREGROUND = '#ffffff';

/** `#rgb`, `#rrggbb` and `#rrggbbaa` as three channels of 0 to 255, or null. */
export function parseHexColor(value: string): [number, number, number] | null {
  const text = value.trim();
  const match = /^#([0-9a-fA-F]{3,8})$/.exec(text);
  const digits = match?.[1];
  if (digits === undefined) return null;
  if (digits.length === 3) {
    const twice = (index: number): number =>
      Number.parseInt(`${digits.slice(index, index + 1)}${digits.slice(index, index + 1)}`, 16);
    return [twice(0), twice(1), twice(2)];
  }
  if (digits.length === 6 || digits.length === 8) {
    return [
      Number.parseInt(digits.slice(0, 2), 16),
      Number.parseInt(digits.slice(2, 4), 16),
      Number.parseInt(digits.slice(4, 6), 16),
    ];
  }
  return null;
}

function channelLuminance(channel: number): number {
  const value = channel / 255;
  return value <= 0.04045 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
}

/** Relative luminance, WCAG 2.1. */
export function relativeLuminance(color: string): number | null {
  const parsed = parseHexColor(color);
  if (parsed === null) return null;
  const [red, green, blue] = parsed;
  return (
    0.2126 * channelLuminance(red) +
    0.7152 * channelLuminance(green) +
    0.0722 * channelLuminance(blue)
  );
}

/** The contrast ratio between two colours, from 1 to 21, or null when either
 *  is not a colour this reads. */
export function contrastRatio(one: string, other: string): number | null {
  const first = relativeLuminance(one);
  const second = relativeLuminance(other);
  if (first === null || second === null) return null;
  const lighter = Math.max(first, second);
  const darker = Math.min(first, second);
  return (lighter + 0.05) / (darker + 0.05);
}

/** The foreground to write over `background`: the one of the two that reads
 *  better over it. An unreadable value takes the light foreground, which is
 *  what the product's own dark surfaces use. */
export function foregroundOver(background: string | null | undefined): string {
  if (background === null || background === undefined || background === '') {
    return LIGHT_FOREGROUND;
  }
  const light = contrastRatio(background, LIGHT_FOREGROUND);
  const dark = contrastRatio(background, DARK_FOREGROUND);
  if (light === null || dark === null) return LIGHT_FOREGROUND;
  return dark > light ? DARK_FOREGROUND : LIGHT_FOREGROUND;
}
